import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import Popover from "antd/es/popover";
import cn from "classnames";
import deepmerge from "deepmerge";

import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { DataAttributesProps } from "@mapmycustomers/shared/types/dataAttributes";
import {
  EntityType,
  EntityTypesSupportedByMapsPage,
  EntityTypesSupportingRoutes,
  Group,
  MapFilterEntityType,
  Route,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { FilterOptionValue } from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import MapFilterModel, {
  ModifiedMapFilterModel,
} from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";

import FilterButton from "@app/component/filtering/components/FilterButton";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import useMapView from "@app/component/view/utils/useMapView";
import configService from "@app/config/ConfigService";
import localSettings from "@app/config/LocalSettings";
import Environment from "@app/enum/Environment";
import { getActivityTypes } from "@app/store/activity";
import { getFunnels } from "@app/store/deal";
import { getCompanyGroups, getDealGroups, getPeopleGroups } from "@app/store/groups";
import { areCheckInFieldsVisible, getColors, getPosition } from "@app/store/iam";
import { setOfferedFilters } from "@app/store/map/actions";
import { getOfferedFilters } from "@app/store/map/selectors";
import { getAllUsersOfEntireOrganization, getTeamsOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { companyRoutes, peopleRoutes } from "@app/store/routes/selector";
import { getSelectedFilter } from "@app/store/savedFilters";
import { areEqualIgnoreOrder } from "@app/util/arrayUtils";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import loggingService from "@app/util/logging";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import arePropsEqual from "@app/util/react/arePropsEqual";

import FilterDropdown from "./components/FilterDropdown";
import Header from "./components/Header";
import styles from "./MapFilterButton.module.scss";
import cleanUpFilterModel from "./utils/cleanUpFilterModel";
import { FILTER_ENTITY_TYPES } from "./utils/consts";
import getAppliedMapFiltersCount from "./utils/getAppliedMapFiltersCount";
import hasIncompleteValue from "./utils/hasIncompleteValue";
import isFilterChanged from "./utils/isFilterChanged";
import useFilteredFields from "./utils/useFilteredFields";
import useSavedMapFilterApplicator from "./utils/useSavedMapFilterApplicator";

interface MapFilterButtonProps extends DataAttributesProps {
  className?: string;
  context: string;
  entityTypes?: MapFilterEntityType[];
  // Fields which are not available for selection in field dropdown. Maybe we use quick filters and don't
  // want to duplicate them there and in filter dropdown. Or maybe some of them are implicit like
  // startAt field on Calendar View for activities and we don't give user an ability to edit these filters
  // directly. But even though we have a separate prop for such implicit fields (implicitFilterFields),
  // we wanna give more flexibility and encourage to specify such fields in hiddenFields prop again.
  hiddenFields?: IField[];
  hideSavedFilters?: boolean;
  // A list of implicit fields, see example above. For saved filters, we do not include any filter rules for
  // implicit fields into "total filters applied" number. We also preserve any existing filter rules for
  // implicit fields when applying a saved filter even if saved filter has a rule for such field too.
  implicitFilterFields?: IField[];
  isSavedFilterAvailable?: (savedFilter: MapSavedFilter) => boolean;
  // a function to process saved filter before applying it
  processSavedFilter?: (savedFilter: MapSavedFilter) => MapSavedFilter | undefined;
  selectedFilter?: MapSavedFilter;
  zIndex?: number;
}

interface Props extends MapFilterButtonProps {
  checkInFieldsVisibility: boolean;
  filterOptions?: Partial<Record<FilterOption, FilterOptionValue>>;
  groups: Record<EntityTypesSupportedByMapsPage, Group[]>;
  // Offers user to filter by some field. Intended use is to provide a filter by some field with some
  // operator, but without a value.
  offerFilters?: ModifiedMapFilterModel;
  routes: Record<EntityTypesSupportingRoutes, Route[]>;
  setOfferedFilters: typeof setOfferedFilters;
}

const MapFilterButton: React.FC<Props> = ({
  checkInFieldsVisibility,
  className,
  context,
  dataAttributes,
  entityTypes = FILTER_ENTITY_TYPES,
  filterOptions,
  groups,
  hiddenFields,
  hideSavedFilters,
  implicitFilterFields,
  isSavedFilterAvailable,
  offerFilters,
  processSavedFilter,
  routes,
  selectedFilter,
  setOfferedFilters,
  zIndex,
}) => {
  const analytics = useAnalytics();

  const {
    updateFilterModel,
    updateViewAs,
    updateViewState,
    viewState: { filter, viewAs, visibleEntities },
  } = useMapView();

  const [warningVisible, setWarningVisibility] = useState(false);
  const userHidWarning = useRef(false);

  useEffect(() => {
    if (
      configService.getCurrentEnvironment() === Environment.DEVELOPMENT ||
      localSettings.wasSkeletonKeyUsed()
    ) {
      window.__mmc_reset_filters__ = () => {
        const skipFieldsFilterNames = new Set(
          (implicitFilterFields ?? []).map(({ filterName }) => filterName)
        );
        const res = FILTER_ENTITY_TYPES.reduce<ModifiedMapFilterModel>((result, entityType) => {
          const fieldModel = getFieldModelByEntityType(entityType);

          let processedFilters = fieldModel.fields.reduce<Partial<FilterModel>>(
            (result, field) =>
              skipFieldsFilterNames.has(field.filterName)
                ? result
                : Object.assign(result, { [field.filterName]: undefined }),
            {}
          );

          // if all are fields are undefined and none of them is in skipped list, mark whole entityType as empty
          if (
            !fieldModel.fields.some((field) => skipFieldsFilterNames.has(field.name)) &&
            Object.keys(processedFilters).every((fieldName) => !processedFilters[fieldName])
          ) {
            processedFilters = {};
          }

          return Object.assign(result, {
            [entityType]: Object.keys(processedFilters).length ? processedFilters : undefined,
          });
        }, {});
        updateFilterModel(res);
        updateViewAs(undefined);
      };
    }
    return () => {
      delete window.__mmc_reset_filters__;
    };
  }, [implicitFilterFields, updateFilterModel, updateViewAs]);

  const updateWarningState = useCallback(
    (before: MapFilterModel, after: ModifiedMapFilterModel) => {
      const usedEntityFiltersBefore = MAP_ENTITY_TYPES.filter((entityType) =>
        isDefined(before[entityType])
      );
      const usedEntityFiltersAfter = MAP_ENTITY_TYPES.filter((entityType) =>
        isDefined(after[entityType])
      );

      if (usedEntityFiltersAfter.length === 0) {
        setWarningVisibility(false);
        return;
      }

      const usedEntityFiltersChanged = !areEqualIgnoreOrder(
        usedEntityFiltersBefore,
        usedEntityFiltersAfter
      );

      const visibleEntitiesDiffer = !areEqualIgnoreOrder(visibleEntities, usedEntityFiltersAfter);

      if (usedEntityFiltersAfter.length <= 2 && (usedEntityFiltersChanged || warningVisible)) {
        if (!userHidWarning.current && visibleEntitiesDiffer) {
          setWarningVisibility(true);
          updateViewState({ visibleEntities: usedEntityFiltersAfter });
        }
      } else {
        setWarningVisibility(false);
      }
    },
    [updateViewState, warningVisible, userHidWarning, visibleEntities]
  );

  const applyFilterChanges = useCallback(
    (modifiedFilterModel: ModifiedMapFilterModel) => {
      const updatedFilterModel = cleanUpFilterModel(modifiedFilterModel);
      if (isFilterChanged(filter, updatedFilterModel)) {
        updateWarningState(filter, updatedFilterModel);
        updateFilterModel(updatedFilterModel);
      }
      // Ignoring because we know that fake viewAs field is always a SimpleCondition
      // @ts-ignore
      const updatedViewAs: undefined | User["id"] = modifiedFilterModel.universal?.viewAs?.value;
      if (viewAs !== updatedViewAs) {
        updateViewAs(updatedViewAs);
      }
    },
    [filter, updateFilterModel, updateViewAs, viewAs, updateWarningState]
  );

  const [modifiedFilterModel, setModifiedFilterModel, resetFilterModel] =
    useStateWithDebouncedListener(
      [
        useDynamicCallback((modifiedFilterModel: ModifiedMapFilterModel) => {
          if (!hasIncompleteValue(modifiedFilterModel)) {
            applyFilterChanges(modifiedFilterModel);
          }
        }),
        500,
      ],
      {} as ModifiedMapFilterModel
    );

  useEffect(() => {
    // calling resetFilterModel instead of setModifiedFilterModel will not cause state callback to fire
    // also, let's use a fake viewAs filter, which is needed for simpler viewAs integration into filters
    resetFilterModel({
      ...filter,
      universal: viewAs
        ? {
            ...(filter.universal || {}),
            viewAs: { operator: FilterOperator.EQUALS, value: viewAs },
          }
        : filter.universal,
    });
  }, [filter, resetFilterModel, viewAs]);
  const [popoverVisible, setPopoverVisibility] = useState<boolean>(false);

  const handleClickPopoverVisibilityChange = useCallback(
    (visible) => {
      setPopoverVisibility(visible);
      if (!visible) {
        // apply filters when popover is closed
        applyFilterChanges(modifiedFilterModel);
        // reset offered filters
        setOfferedFilters(undefined);
        // reset warning
        setWarningVisibility(false);
        userHidWarning.current = false;
      }
      analytics?.clicked(["Filters"], { visible });
    },
    [analytics, applyFilterChanges, modifiedFilterModel, setOfferedFilters]
  );

  useEffect(() => {
    if (offerFilters) {
      setModifiedFilterModel((currentModel) => deepmerge(currentModel, offerFilters));
      setPopoverVisibility(true);
    }
  }, [offerFilters, setModifiedFilterModel]);

  const filteredFields = useFilteredFields(modifiedFilterModel, hiddenFields);

  const filterCount = useMemo(
    () =>
      getAppliedMapFiltersCount(
        cleanUpFilterModel(modifiedFilterModel),
        viewAs,
        implicitFilterFields
      ),
    [modifiedFilterModel, implicitFilterFields, viewAs]
  );

  const handleUpdateSavedFilterState = useCallback(
    (before: MapFilterModel, after: ModifiedMapFilterModel) => {
      userHidWarning.current = false;
      updateWarningState(before, after);
    },
    [updateWarningState]
  );

  useSavedMapFilterApplicator(
    entityTypes,
    EntityType.PIN,
    selectedFilter,
    implicitFilterFields,
    processSavedFilter,
    handleUpdateSavedFilterState
  );

  const focusedRule: { entityType: MapFilterEntityType; fieldName: string } | undefined =
    useMemo(() => {
      if (!offerFilters) {
        return undefined;
      }
      // here we assume that offerFilters will not have keys with undefined/null values
      return Object.entries(offerFilters).map(([entityType, filterModel]) => ({
        entityType: entityType as MapFilterEntityType,
        fieldName: Object.keys(filterModel)[0],
      }))[0];
    }, [offerFilters]);

  const handleHideWarning = useCallback(() => {
    userHidWarning.current = true;
  }, []);

  loggingService.debug("modifiedFilterModel=", modifiedFilterModel);

  return (
    <Popover
      content={
        popoverVisible ? (
          <FilterDropdown
            checkInFieldsVisibility={checkInFieldsVisibility}
            context={context}
            entityTypes={entityTypes}
            filteredFields={filteredFields}
            filterModel={modifiedFilterModel}
            filterOptions={filterOptions}
            focus={focusedRule}
            groups={groups}
            hiddenFields={hiddenFields}
            hideSavedFilters={hideSavedFilters}
            implicitFilterFields={implicitFilterFields}
            onChange={setModifiedFilterModel}
            onHideWarning={handleHideWarning}
            routes={routes}
            visibleEntities={visibleEntities}
            // we do not show warning again in a current session
            warningVisible={warningVisible && !userHidWarning.current}
          />
        ) : null
      }
      destroyTooltipOnHide
      onVisibleChange={handleClickPopoverVisibilityChange}
      overlayClassName={cn(styles.popoverContainer, "mmc-data_map-filter-popover")}
      placement="bottomLeft"
      title={
        <Header
          hideSavedFilters={hideSavedFilters}
          implicitFilterFields={implicitFilterFields}
          isSavedFilterAvailable={isSavedFilterAvailable}
          selectedFilter={selectedFilter}
        />
      }
      trigger="click"
      visible={popoverVisible}
      zIndex={zIndex}
    >
      <Interactive id="map-filter-button">
        <FilterButton
          className={cn(styles.container, { [styles.whiteButton]: !popoverVisible }, className)}
          dataAttributes={dataAttributes}
          filterCount={filterCount}
          onClick={() => handleClickPopoverVisibilityChange(!popoverVisible)}
          pressed={popoverVisible}
          selectedSavedFilter={selectedFilter}
        />
      </Interactive>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  checkInFieldsVisibility: areCheckInFieldsVisible(state),
  filterOptions: {
    [FilterOption.AVAILABLE_ACTIVITY_TYPES]: getActivityTypes(state) ?? [],
    [FilterOption.AVAILABLE_COLORS]: getColors(state) ?? [],
    [FilterOption.AVAILABLE_FUNNELS]: getFunnels(state) ?? [],
    [FilterOption.AVAILABLE_TEAMS]: getTeamsOfEntireOrganization(state) ?? [],
    [FilterOption.AVAILABLE_USERS]: getAllUsersOfEntireOrganization(state) ?? [],
    [FilterOption.USER_POSITION]: getPosition(state),
  },
  groups: {
    [EntityType.COMPANY]: getCompanyGroups(state),
    [EntityType.DEAL]: getDealGroups(state),
    [EntityType.PERSON]: getPeopleGroups(state),
  },
  offerFilters: getOfferedFilters(state),
  routes: {
    [EntityType.COMPANY]: companyRoutes(state),
    [EntityType.PERSON]: peopleRoutes(state),
  },
  selectedFilter: getSelectedFilter(state)(EntityType.PIN) as MapSavedFilter,
});

const mapDispatchToProps = {
  setOfferedFilters,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  memo(MapFilterButton, (prevProps: Props, nextProps: Props) => {
    if (prevProps.filterOptions && nextProps.filterOptions) {
      const filterOptionsEqual = (
        Object.keys(nextProps.filterOptions) as Array<keyof Props["filterOptions"]>
      ).every((key) => Object.is(prevProps.filterOptions?.[key], nextProps.filterOptions?.[key]));
      if (!filterOptionsEqual) {
        return false;
      }
    }

    if (prevProps.groups && nextProps.groups) {
      const groupsEqual = (Object.keys(nextProps.groups) as Array<keyof Props["groups"]>).every(
        (key) => Object.is(prevProps.groups[key], nextProps.groups[key])
      );
      if (!groupsEqual) {
        return false;
      }
    }

    if (prevProps.routes && nextProps.routes) {
      const routesEqual = (Object.keys(nextProps.routes) as Array<keyof Props["routes"]>).every(
        (key) => Object.is(prevProps.routes[key], nextProps.routes[key])
      );
      if (!routesEqual) {
        return false;
      }
    }

    return arePropsEqual(prevProps, nextProps, ["filterOptions", "groups", "routes"]);
  })
);
